// ControlValues = the state of the reference tool's form controls, keyed by the HTML control ids. The ported
// functions read/write it where the HTML reads/writes `$('id').value/.checked`. Numbers are NaN when the field is
// empty (parseFloat semantics), selects are strings ('' when the select has no options), checkboxes booleans.

import type { EngineCatalog } from "./catalog";
import { materialIdOf } from "./catalog";
import { LOAD_DURATION_FROM_HTML, LOAD_DURATION_TO_HTML } from "./data";
import type { CalculationInput, CltLayerInput, ConnectionType } from "./types";

// One row of the "Definizione utente" CLT layers (.clt-ul-spessore / .clt-ul-dir / .clt-ul-classe).
export type CltUserLayer = { spessore: number; orientamento: number; classe: string };

export type ControlValues = {
  materiale1: ConnectionType;
  thetaNum: number;

  mat1Tipo: string;
  mat1Specie: string;
  mat1Classe: string;
  mat1CustomName: string;
  rhok1: number;
  rhomean1: number;
  fi1: number; // 0 | 90
  eps1: number;
  eps1Auto: boolean;
  continuo1: boolean;
  b1: number;
  d1: number;
  spessore1: number;
  douglas1: boolean;
  a4reduce1: boolean;
  a4t1: number;
  a4c1: number;
  a3t1: number;
  a1e1: number; // informative (autoFillSpacing)
  a2e1: number;
  clt1Lato: string;
  clt1NumStrati: number;
  clt1Disposizione: string;
  clt1SpessoriMode: string;
  clt1Spessore0: number;
  clt1Spessore90: number;
  clt1Classe: string;
  clt1UserLayers: CltUserLayer[];

  mat2Tipo: string;
  mat2Specie: string;
  mat2Classe: string;
  mat2CustomName: string;
  rhok2: number;
  rhomean2: number;
  fi2: number;
  eps2: number;
  eps2Auto: boolean;
  continuo2: boolean;
  b2: number;
  d2: number;
  spessore2: number;
  douglas2: boolean;
  a4reduce2: boolean;
  a4t2: number;
  a4c2: number;
  a3t2: number;
  a3c2: number;
  a1e2: number;
  a2e2: number;
  clt2Lato: string;
  clt2NumStrati: number;
  clt2Disposizione: string;
  clt2SpessoriMode: string;
  clt2Spessore0: number;
  clt2Spessore90: number;
  clt2Classe: string;
  clt2UserLayers: CltUserLayer[];

  forceFn: number;
  forceFv: number;
  forceFa: number;
  classeServizio: number;
  durataCarico: string; // HTML key: permanente | lungaDurata | medioTermine | breveTermine | istantanea
  normativaGammaM: string; // EC5 | NTC2018
  gammaMColonna: string; // A | B
  gammaM: number;
  gammaMAuto: boolean;

  etaFamiglia: string;
  etaDiametro: number;
  etaLunghezza: number;
  etaForo: string; // predrilled | nonPredrilled
  etaForoUser: string | null; // #etaForo dataset.userValue: the user's choice while the ETA rule forces "predrilled"
  useRondella: boolean;
  rondellaCodice: string;
  autoSpacing: boolean;

  rows: number;
  cols: number;
  s11: number;
  s12: number;
  centroOrizz: number;
  centroVert: number;
  spreadViti: boolean;
  sfalsaViti: boolean;
  sfalsaDueLati: boolean;
  limitGriglia: boolean;
  limitaGrigliaRadente: boolean;

  // HTML globals (not controls) persisted with the calculation
  rowsLinkedToMax: boolean;
  colsLinkedToMax: boolean;
  centroLinkedToMax: boolean;
};

const n = (v: number | null | undefined): number => (v == null ? NaN : v);
const orDefault = (v: number | null | undefined, def: number): number => (v == null ? def : v);
const toNull = (v: number): number | null => (Number.isFinite(v) ? v : null);

function layersToControls(layers: CltLayerInput[] | null): CltUserLayer[] {
  return (layers ?? [])
    .slice()
    .sort((a, b) => a.position - b.position)
    .map((l) => ({ spessore: n(l.thickness), orientamento: l.orientation, classe: l.classe }));
}

function layersFromControls(layers: CltUserLayer[]): CltLayerInput[] {
  return layers.map((l, i) => ({
    position: i,
    thickness: l.spessore,
    orientation: l.orientamento === 90 ? 90 : 0,
    classe: l.classe,
  }));
}

// CalculationInput -> controls. Defaults (null) follow the HTML start state: required numeric fields stay empty
// (NaN), the other controls take their markup default. Material densities left null here are filled by the engine
// from the selected class (applyMatClasse), see render.ts.
export function toControlValues(input: CalculationInput, cat: EngineCatalog): ControlValues {
  const m1 = input.element1TimberMaterialId ? cat.materialById.get(input.element1TimberMaterialId) : undefined;
  const m2 = input.element2TimberMaterialId ? cat.materialById.get(input.element2TimberMaterialId) : undefined;
  const screw = input.screwId ? cat.screwById.get(input.screwId) : undefined;
  return {
    materiale1: input.connectionType ?? "legno",
    thetaNum: n(input.theta),

    mat1Tipo: m1?.tipo ?? "",
    mat1Specie: m1?.specie ?? "",
    mat1Classe: m1?.classe ?? "",
    mat1CustomName: input.element1CustomMaterialName ?? "",
    rhok1: n(input.element1CharacteristicDensity),
    rhomean1: n(input.element1MeanDensity),
    fi1: input.element1FiberOrientation ?? 0,
    eps1: orDefault(input.element1ScrewGrainAngle, 90),
    eps1Auto: input.element1ScrewGrainAngleAuto,
    continuo1: input.element1Continuous,
    b1: n(input.element1Width),
    d1: n(input.element1Depth),
    spessore1: n(input.element1Thickness),
    douglas1: input.element1DouglasFir,
    a4reduce1: input.element1ReduceUnloadedEdgeDistance,
    a4t1: n(input.element1EdgeDistanceA4t),
    a4c1: n(input.element1EdgeDistanceA4c),
    a3t1: n(input.element1EdgeDistanceA3t),
    a1e1: n(input.element1EdgeDistanceA1e),
    a2e1: n(input.element1EdgeDistanceA2e),
    clt1Lato: input.element1CltLato ?? "narrow",
    clt1NumStrati: orDefault(input.element1CltNumStrati, 3),
    clt1Disposizione: input.element1CltDisposizione ?? "fisso",
    clt1SpessoriMode: input.element1CltSpessoriMode ?? "uniforme",
    clt1Spessore0: orDefault(input.element1CltSpessore0, 20),
    clt1Spessore90: orDefault(input.element1CltSpessore90, 20),
    clt1Classe: input.element1CltClasse ?? "C24",
    clt1UserLayers: layersToControls(input.element1CltLayers),

    mat2Tipo: m2?.tipo ?? "",
    mat2Specie: m2?.specie ?? "",
    mat2Classe: m2?.classe ?? "",
    mat2CustomName: input.element2CustomMaterialName ?? "",
    rhok2: n(input.element2CharacteristicDensity),
    rhomean2: n(input.element2MeanDensity),
    fi2: input.element2FiberOrientation ?? 0,
    eps2: orDefault(input.element2ScrewGrainAngle, 90),
    eps2Auto: input.element2ScrewGrainAngleAuto,
    continuo2: input.element2Continuous,
    b2: n(input.element2Width),
    d2: n(input.element2Depth),
    spessore2: n(input.element2Thickness),
    douglas2: input.element2DouglasFir,
    a4reduce2: input.element2ReduceUnloadedEdgeDistance,
    a4t2: n(input.element2EdgeDistanceA4t),
    a4c2: n(input.element2EdgeDistanceA4c),
    a3t2: n(input.element2EdgeDistanceA3t),
    a3c2: n(input.element2EdgeDistanceA3c),
    a1e2: n(input.element2EdgeDistanceA1e),
    a2e2: n(input.element2EdgeDistanceA2e),
    clt2Lato: input.element2CltLato ?? "narrow",
    clt2NumStrati: orDefault(input.element2CltNumStrati, 3),
    clt2Disposizione: input.element2CltDisposizione ?? "fisso",
    clt2SpessoriMode: input.element2CltSpessoriMode ?? "uniforme",
    clt2Spessore0: orDefault(input.element2CltSpessore0, 20),
    clt2Spessore90: orDefault(input.element2CltSpessore90, 20),
    clt2Classe: input.element2CltClasse ?? "C24",
    clt2UserLayers: layersToControls(input.element2CltLayers),

    forceFn: n(input.forceFn),
    forceFv: n(input.forceFv),
    forceFa: n(input.forceFa),
    classeServizio: input.serviceClassCode ?? 2,
    durataCarico: input.loadDurationClassCode
      ? (LOAD_DURATION_TO_HTML[input.loadDurationClassCode] ?? input.loadDurationClassCode)
      : "medioTermine",
    normativaGammaM: input.normativeStandard ?? "EC5",
    gammaMColonna: input.gammaMColumn ?? "A",
    gammaM: n(input.gammaMValue),
    gammaMAuto: input.gammaMAuto,

    etaFamiglia: screw?.family ?? "",
    etaDiametro: screw ? screw.geometria_mm.d1 : NaN,
    etaLunghezza: screw ? screw.geometria_mm.L : NaN,
    etaForo: input.preDrilled ? "predrilled" : "nonPredrilled",
    etaForoUser: null,
    useRondella: input.washerCode != null && input.washerCode !== "",
    rondellaCodice: input.washerCode ?? "",
    autoSpacing: input.autoSpacing,

    rows: orDefault(input.gridRows, 3),
    cols: orDefault(input.gridCols, 2),
    s11: n(input.spacingS1),
    s12: n(input.spacingS2),
    centroOrizz: input.gridOffsetX ?? 0,
    centroVert: input.gridOffsetY ?? 0,
    spreadViti: input.spreadScrews,
    sfalsaViti: input.staggerRows,
    sfalsaDueLati: input.staggerTwoSides,
    limitGriglia: input.limitGridToMax,
    limitaGrigliaRadente: input.limitGridDiagonalAtGrazingAngle,

    rowsLinkedToMax: input.gridRowsLinkedToMax,
    colsLinkedToMax: input.gridColsLinkedToMax,
    centroLinkedToMax: input.gridOffsetLinkedToMax,
  };
}

// currentScrewRecord() on the controls: first record matching family + diameter (+ length when valid).
export function screwRecordOf(v: ControlValues, cat: EngineCatalog) {
  const candidati = cat.SCREWS_DB.filter((s) => s.family === v.etaFamiglia && s.geometria_mm.d1 === v.etaDiametro);
  const l = v.etaLunghezza;
  if (Number.isFinite(l)) {
    const match = candidati.find((s) => s.geometria_mm.L === l);
    if (match) return match;
  }
  return candidati[0] || null;
}

// Controls -> CalculationInput fields (inverse of toControlValues).
export function fromControlValues(v: ControlValues, cat: EngineCatalog): Omit<CalculationInput, never> {
  const screw = v.etaFamiglia ? screwRecordOf(v, cat) : null;
  const fo = (x: number): 0 | 90 => (x === 90 ? 90 : 0);
  return {
    connectionType: v.materiale1,
    theta: toNull(v.thetaNum),

    element1TimberMaterialId: materialIdOf(cat, v.mat1Tipo, v.mat1Specie, v.mat1Classe),
    element1CustomMaterialName: v.mat1CustomName || null,
    element1CharacteristicDensity: toNull(v.rhok1),
    element1MeanDensity: toNull(v.rhomean1),
    element1FiberOrientation: fo(v.fi1),
    element1ScrewGrainAngle: toNull(v.eps1),
    element1ScrewGrainAngleAuto: v.eps1Auto,
    element1Continuous: v.continuo1,
    element1Width: toNull(v.b1),
    element1Depth: toNull(v.d1),
    element1Thickness: toNull(v.spessore1),
    element1DouglasFir: v.douglas1,
    element1ReduceUnloadedEdgeDistance: v.a4reduce1,
    element1EdgeDistanceA1e: toNull(v.a1e1),
    element1EdgeDistanceA2e: toNull(v.a2e1),
    element1EdgeDistanceA3t: toNull(v.a3t1),
    element1EdgeDistanceA4t: toNull(v.a4t1),
    element1EdgeDistanceA4c: toNull(v.a4c1),
    element1CltLato: v.clt1Lato === "wide" ? "wide" : "narrow",
    element1CltNumStrati: toNull(v.clt1NumStrati),
    element1CltDisposizione: (v.clt1Disposizione || null) as CalculationInput["element1CltDisposizione"],
    element1CltSpessoriMode: v.clt1SpessoriMode === "differenziato" ? "differenziato" : "uniforme",
    element1CltSpessore0: toNull(v.clt1Spessore0),
    element1CltSpessore90: toNull(v.clt1Spessore90),
    element1CltClasse: v.clt1Classe || null,
    element1CltLayers: layersFromControls(v.clt1UserLayers),

    element2TimberMaterialId: materialIdOf(cat, v.mat2Tipo, v.mat2Specie, v.mat2Classe),
    element2CustomMaterialName: v.mat2CustomName || null,
    element2CharacteristicDensity: toNull(v.rhok2),
    element2MeanDensity: toNull(v.rhomean2),
    element2FiberOrientation: fo(v.fi2),
    element2ScrewGrainAngle: toNull(v.eps2),
    element2ScrewGrainAngleAuto: v.eps2Auto,
    element2Continuous: v.continuo2,
    element2Width: toNull(v.b2),
    element2Depth: toNull(v.d2),
    element2Thickness: toNull(v.spessore2),
    element2DouglasFir: v.douglas2,
    element2ReduceUnloadedEdgeDistance: v.a4reduce2,
    element2EdgeDistanceA1e: toNull(v.a1e2),
    element2EdgeDistanceA2e: toNull(v.a2e2),
    element2EdgeDistanceA3t: toNull(v.a3t2),
    element2EdgeDistanceA3c: toNull(v.a3c2),
    element2EdgeDistanceA4t: toNull(v.a4t2),
    element2EdgeDistanceA4c: toNull(v.a4c2),
    element2CltLato: v.clt2Lato === "wide" ? "wide" : "narrow",
    element2CltNumStrati: toNull(v.clt2NumStrati),
    element2CltDisposizione: (v.clt2Disposizione || null) as CalculationInput["element2CltDisposizione"],
    element2CltSpessoriMode: v.clt2SpessoriMode === "differenziato" ? "differenziato" : "uniforme",
    element2CltSpessore0: toNull(v.clt2Spessore0),
    element2CltSpessore90: toNull(v.clt2Spessore90),
    element2CltClasse: v.clt2Classe || null,
    element2CltLayers: layersFromControls(v.clt2UserLayers),

    forceFn: toNull(v.forceFn),
    forceFv: toNull(v.forceFv),
    forceFa: toNull(v.forceFa),
    serviceClassCode: (Number.isFinite(v.classeServizio) ? v.classeServizio : null) as CalculationInput["serviceClassCode"],
    loadDurationClassCode: LOAD_DURATION_FROM_HTML[v.durataCarico] ?? (v.durataCarico || null),
    normativeStandard: (v.normativaGammaM || null) as CalculationInput["normativeStandard"],
    gammaMColumn: v.gammaMColonna === "B" ? "B" : "A",
    gammaMAuto: v.gammaMAuto,
    gammaMValue: toNull(v.gammaM),

    screwId: screw ? screw.id : null,
    preDrilled: (v.etaForoUser ?? v.etaForo) === "predrilled", // the user's choice, not the one forced by the ETA
    washerCode: v.useRondella && v.rondellaCodice ? v.rondellaCodice : null,
    autoSpacing: v.autoSpacing,

    gridRows: toNull(v.rows),
    gridCols: toNull(v.cols),
    spacingS1: toNull(v.s11),
    spacingS2: toNull(v.s12),
    gridOffsetX: Number.isFinite(v.centroOrizz) ? v.centroOrizz : 0,
    gridOffsetY: Number.isFinite(v.centroVert) ? v.centroVert : 0,
    staggerRows: v.sfalsaViti,
    staggerTwoSides: v.sfalsaDueLati,
    spreadScrews: v.spreadViti,
    limitGridToMax: v.limitGriglia,
    limitGridDiagonalAtGrazingAngle: v.limitaGrigliaRadente,
    gridRowsLinkedToMax: v.rowsLinkedToMax,
    gridColsLinkedToMax: v.colsLinkedToMax,
    gridOffsetLinkedToMax: v.centroLinkedToMax,
  };
}

const sameValue = (a: unknown, b: unknown): boolean => {
  if (a === b) return true;
  if (typeof a === "number" && typeof b === "number") return Number.isNaN(a) && Number.isNaN(b);
  if (Array.isArray(a) || Array.isArray(b) || (a && typeof a === "object") || (b && typeof b === "object")) {
    return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
  }
  return false;
};

// Fields of `input` whose value differs from what the engine displays (auto-filled distances/spacings, rows/cols
// following the maximum, CLT-derived geometry, eps auto, gammaM auto, list fallbacks of screw/washer/material...).
// The UI writes this patch back into the form so the persisted calculation matches the screen, like the HTML DOM.
// CLT layers are only compared when the input has some (the HTML only creates the rows for "userDefined").
export function inputPatch(input: CalculationInput, v: ControlValues, cat: EngineCatalog): Partial<CalculationInput> {
  const next = fromControlValues(v, cat);
  const patch: Partial<CalculationInput> = {};
  for (const key of Object.keys(next) as (keyof CalculationInput)[]) {
    if (key === "element1CltLayers" || key === "element2CltLayers") {
      const cur = input[key] ?? [];
      const nv = next[key] ?? [];
      if (nv.length === 0 && cur.length === 0) continue;
      if (sameValue(layersToControls(cur), layersToControls(nv))) continue;
    }
    if (!sameValue(input[key], next[key])) (patch as Record<string, unknown>)[key] = next[key];
  }
  return patch;
}
